/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// API
import { apiLogin, setUserData } from "api/auth";
import { getCurrentUser } from "utils/userUtils";
import { useMaterialUIController, setUser } from "context";

function Basic() {
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();
  const [formData, setFormData] = useState({
    idPengguna: "",
    katalaluan: "",
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If session already active, redirect to their landing page immediately
  useEffect(() => {
    const existing = getCurrentUser();
    if (existing) {
      const { getDefaultRoute } = require("utils/permissions");
      const target = getDefaultRoute(existing);
      navigate(target, { replace: true });
    }
  }, []);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
    // Clear error when user types
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.idPengguna || !formData.katalaluan) {
      setError("Sila masukkan ID Pengguna dan Katalaluan");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiLogin(formData.idPengguna, formData.katalaluan);

      if (response.success) {
        // Store user data
        setUserData(response.user);
        // Update context
        setUser(dispatch, response.user);

        // Navigate based on user's menu type and department
        const { getDefaultRoute } = require("utils/permissions");
        const target = getDefaultRoute(response.user);
        navigate(target);
      } else {
        setError(response.error || "Login gagal");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("ID Pengguna atau Katalaluan tidak sah");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout image={null}>
      <Card
        sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 4,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
        }}
      >
        {/* Mobile-only logo at top */}
        <MDBox
          sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            alignItems: 'center',
            pt: 4,
            pb: 2,
          }}
        >
          <img
            src="/LogoTb.png"
            alt="STAIL Logo"
            style={{
              width: "120px",
              height: "120px",
              objectFit: "contain",
              marginBottom: 16,
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          />
          <MDTypography
            variant="subtitle2"
            sx={{
              color: "#f4c542",
              fontWeight: 600,
              letterSpacing: 1.5,
              fontSize: '0.75rem',
              textAlign: 'center',
              px: 2,
            }}
          >
            LEMBAGA KEMAJUAN JOHOR TENGGARA
          </MDTypography>
        </MDBox>

        {/* Form Content */}
        <MDBox pt={{ xs: 3, md: 5 }} pb={{ xs: 3, md: 5 }} px={{ xs: 3, md: 4 }}>
          <MDTypography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 3,
              textAlign: 'center',
            }}
          >
            Log Masuk
          </MDTypography>

          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={3}>
              <MDInput
                type="text"
                label="ID Pengguna"
                fullWidth
                value={formData.idPengguna}
                onChange={handleInputChange("idPengguna")}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(244, 197, 66, 0.5)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#ffffff',
                  },
                }}
              />
            </MDBox>
            <MDBox mb={3}>
              <MDInput
                type="password"
                label="Katalaluan"
                fullWidth
                value={formData.katalaluan}
                onChange={handleInputChange("katalaluan")}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(244, 197, 66, 0.5)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#ffffff',
                  },
                }}
              />
            </MDBox>

            {error && (
              <MDBox mb={2}>
                <MDTypography variant="caption" color="error" fontWeight="medium">
                  {error}
                </MDTypography>
              </MDBox>
            )}

            <MDBox mt={4} mb={1}>
              <MDButton
                fullWidth
                type="submit"
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #f4c542 0%, #f39c12 100%)',
                  color: '#0a1628',
                  fontWeight: 600,
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: 3,
                  boxShadow: '0 4px 14px rgba(244, 197, 66, 0.4)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(244, 197, 66, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: 'rgba(244, 197, 66, 0.3)',
                    color: 'rgba(10, 22, 40, 0.5)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? "Sedang log masuk..." : "Log Masuk"}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
